import { useTranslation } from 'react-i18next';
import { Instagram, Facebook, Mail, MapPin } from 'lucide-react';
import { ASSETS } from '@/data/brands';

const navLinks = ['home', 'about', 'products', 'cases', 'contact'];

export default function Footer() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const year = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="relative bg-[#10092E] text-white border-t border-white/8 overflow-hidden">
      <div className="absolute inset-0 dwk-noise" aria-hidden="true" />
      <div className="absolute top-[-20%] left-[10%] w-[600px] h-[600px] rounded-full blur-[120px] pointer-events-none"
           style={{ background: 'radial-gradient(closest-side, rgba(137,72,195,0.25), transparent)' }} aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="grid grid-cols-2 md:grid-cols-12 gap-10">
          <div className="col-span-2 md:col-span-5">
            <a href="#home" className="inline-flex items-center gap-3 group" data-testid="footer-logo">
              <img src={ASSETS.logoWhite} alt="Dowakum" className="h-32 md:h-44 w-auto object-contain drop-shadow-[0_10px_30px_rgba(137,72,195,0.55)]" />
            </a>
            <p className={`mt-6 max-w-sm text-white/60 leading-relaxed ${isAr ? 'font-ar' : 'font-en'}`}>
              {t('footer.tagline')}
            </p>
            <div className="mt-8 flex items-center gap-3">
              <a
                href="https://www.instagram.com/dowakum.iraq/"
                target="_blank" rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="w-10 h-10 rounded-full inline-flex items-center justify-center border border-white/15 text-white/85 hover:border-[#40D3BE] hover:text-[#40D3BE] transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={16} />
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=100083034836178"
                target="_blank" rel="noopener noreferrer"
                data-testid="footer-facebook"
                className="w-10 h-10 rounded-full inline-flex items-center justify-center border border-white/15 text-white/85 hover:border-[#40D3BE] hover:text-[#40D3BE] transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={16} />
              </a>
              <a
                href="mailto:dowakum2023@gmail.com"
                data-testid="footer-email"
                className="w-10 h-10 rounded-full inline-flex items-center justify-center border border-white/15 text-white/85 hover:border-[#40D3BE] hover:text-[#40D3BE] transition-colors"
                aria-label="Email"
              >
                <Mail size={16} />
              </a>
            </div>
          </div>

          <div className="col-span-1 md:col-span-3">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4">{t('footer.nav')}</div>
            <ul className="space-y-3">
              {navLinks.map((k) => (
                <li key={k}>
                  <a href={`#${k}`} className={`text-sm text-white/80 hover:text-[#40D3BE] transition-colors ${isAr ? 'font-ar' : 'font-en'}`}>
                    {t(`nav.${k}`)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-1 md:col-span-4">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4">{t('footer.contact')}</div>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3 text-white/85">
                <Mail size={14} className="mt-1 text-[#40D3BE]" />
                <a href="mailto:dowakum2023@gmail.com" className="hover:text-[#40D3BE] transition-colors">
                  {t('contact.placeholder_email')}
                </a>
              </li>
              <li className={`flex items-start gap-3 text-white/85 ${isAr ? 'font-ar' : 'font-en'}`}>
                <MapPin size={14} className="mt-1 text-[#40D3BE]" />
                <span>{t('contact.placeholder_address')}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="hairline-light my-10" />

        <div className={`text-center text-xs text-white/50 ${isAr ? 'font-ar' : 'font-en'}`}>
          <span>© {year} · {t('footer.copyright')}</span>
        </div>
      </div>
    </footer>
  );
}
